import {
  DeleteOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  WarningFilled,
} from "@ant-design/icons";
import { useGetBrandById } from "@entities/brand/lib/hooks/useGetBrandById";
import { useAddToLot } from "@entities/financeApproval/lib/hooks/useAddToLot";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { FirebaseService, ToastService } from "@shared/lib/services";
import { IDelPackage } from "@shared/lib/types";
import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";
import { formatData } from "@shared/lib/utils/formatData";
import { DeliverablePillSummary } from "@shared/ui";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Input,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  Upload,
  message
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { UploadFile } from "antd/es/upload/interface";
import classNames from "classnames";
import { useEffect, useState } from "react";
import styles from "./PurchaseConfirmation.module.scss";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Dragger } = Upload;

interface Props {
  onClose: () => void;
  briefInfluencers: IBriefInfluencerV2[];
}

// 10% service cost

interface TableData {
  key: string;
  creatorName: string;
  deliverables?: IDelPackage;
  influencerCost: number;
  serviceCost: number;
  brandPrice: number;
  revenue: number;
  revenuePercentage: number;
  type: string;
  deliverableId: string;
}

export function PurchaseConfirmation({ onClose, briefInfluencers }: Props) {
  const { brief } = useBriefStore();

  const [errorInfluencers, setErrorInfluencers] = useState<
    {
      status: string;
      reason: string;
      briefInfluencer: IBriefInfluencerV2;
      reasonType: string;
    }[]
  >([]);

  const { brand, isFetching } = useGetBrandById(brief?.brandId || "");

  const brandBilling = brand?.linkedBilling;

  const { addToLot, isAdded, isAdding } = useAddToLot(
    brief?._id || "",
    setErrorInfluencers
  );

  const serviceCostPercentage = (brandBilling?.serviceChargeRate || 0) * 0.01;
  const [toPurchaseInfluencers, setToPurchaseInfluencers] = useState<
    IBriefInfluencerV2[]
  >(
    briefInfluencers.filter(
      (influencer) =>
        !influencer.isCreatorDeclined && !influencer.isBrandDeclined
    )
  );

  const [deliverableModalProps, setDeliverableModalProps] = useState<{
    visible: boolean;
    deliverableId?: string;
    briefInfluencerIds: string[];
    type: "view" | "create";
  }>({
    visible: false,
    deliverableId: "",
    briefInfluencerIds: [],
    type: "create",
  });

  const [isPurchaseSummary, setIsPurchaseSummary] = useState(true);
  const [uploadedFile, setUploadedFile] = useState<UploadFile | null>(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const [remarks, setRemarks] = useState("");

  // Calculate table data
  const tableData: TableData[] = toPurchaseInfluencers.map((influencer) => {
    const influencerCost = influencer.influencerCost?.cost || 0;
    const brandPrice = influencer.brandCost?.cost || 0;
    const serviceCost = Math.round(brandPrice * serviceCostPercentage);
    const revenue = brandPrice + serviceCost - influencerCost;
    const revenuePercentage = brandPrice > 0 ? (revenue / brandPrice) * 100 : 0;

    return {
      key: influencer._id,
      creatorName: influencer.influencerName,
      deliverables: influencer?.deliverableV2,
      influencerCost,
      serviceCost,
      brandPrice,
      revenue,
      revenuePercentage,
      type: influencer.influencer?.type || "",
      deliverableId: influencer?.deliverableV2?._id || "",
    };
  });

  // Calculate totals
  const totals = tableData.reduce(
    (acc, item) => ({
      influencerCost: acc.influencerCost + item.influencerCost,
      serviceCost: acc.serviceCost + item.serviceCost,
      brandPrice: acc.brandPrice + item.brandPrice,
      revenue: acc.revenue + item.revenue,
    }),
    { influencerCost: 0, serviceCost: 0, brandPrice: 0, revenue: 0 }
  );

  const totalRevenuePercentage =
    totals.brandPrice > 0 ? (totals.revenue / totals.brandPrice) * 100 : 0;

  const handleDelete = (key: string) => {
    setToPurchaseInfluencers((prev) => prev.filter((item) => item._id !== key));
  };

  const handlePurchaseClick = () => {
    setIsPurchaseSummary(false);
  };

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const uploadedUrl = await FirebaseService.uploadFileToFirebase(
        "purchase-proofs",
        file
      );
      setUploadedFileUrl(uploadedUrl);
      ToastService.success("File uploaded successfully");
    } catch (error) {
      ToastService.error("File upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSendForApproval = () => {
    if (!uploadedFileUrl) {
      message.warning("Please attach purchase proof");
      return;
    }
    addToLot({
      ids: toPurchaseInfluencers.map((influencer) => influencer._id),
      remarks,
      purchaseProof: uploadedFileUrl,
    });
  };

  const columns: ColumnsType<TableData> = [
    {
      title: "Creator Name",
      dataIndex: "creatorName",
      key: "creatorName",
      render: (text: string, record: TableData) => (
        <div>
          <div>
            <TextWithTooltip text={text} limit={15} />
          </div>
          {record.type && <Tag color="yellow">{record.type}</Tag>}
        </div>
      ),
    },
    {
      title: "Deliverables",
      key: "deliverables",

      render: (_, record) => {
        if (record.deliverables) {
          return (
            <Flex align="center" gap={2}>
              <DeliverablePillSummary 
                deliverable={{
                  briefId: brief?._id || "",
                  purchaseItems: record?.deliverables?.purchaseItems
                }}
              />
              {/* <Flex vertical>
                <Button
                  type="link"
                  size="small"
                  className={styles.deliverableView}
                  onClick={() =>
                    setDeliverableModalProps({
                      visible: true,
                      deliverableId: record?.deliverableId || "",
                      type: "view",
                      briefInfluencerIds: [record.key],
                    })
                  }
                >
                  View
                </Button>
              </Flex> */}
            </Flex>
          );
        }
        return <div></div>;
      },
    },
    {
      title: <TextWithTooltip text="Influencer Commercial " limit={10} />,
      dataIndex: "influencerCost",
      key: "influencerCost",
      render: (value: number) => `₹ ${formatData(value, "0.00 ")}`,
    },
    {
      title: "Service Cost",
      dataIndex: "serviceCost",
      key: "serviceCost",
      render: (value: number) => `₹ ${formatData(value, "0.00 ")}`,
    },
    {
      title: "Brand Price",
      dataIndex: "brandPrice",
      key: "brandPrice",
      render: (value: number) => `₹ ${formatData(value, "0.00 ")}`,
    },
    {
      title: "Revenue",
      dataIndex: "revenue",
      key: "revenue",
      render: (value: number, record: TableData) => (
        <div>
          <div>
            ₹ {formatData(value, "0.00 ")} (
            {record.revenuePercentage.toFixed(1)}%)
          </div>
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 60,
      render: (_, record) => {
        if (record.key !== "total") {
          return (
            <Button
              type="text"
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.key)}
              danger
            />
          );
        }
        return null;
      },
    },
  ];

  // Add total row
  const dataWithTotal = [
    ...tableData,
    {
      key: "total",
      creatorName: "Total",
      deliverables: undefined,
      influencerCost: totals.influencerCost,
      serviceCost: totals.serviceCost,
      brandPrice: totals.brandPrice,
      revenue: totals.revenue,
      revenuePercentage: totalRevenuePercentage,
      type: "",
      deliverableId: "",
    } as TableData,
  ];

  const uploadProps = {
    name: "file",
    multiple: false,
    fileList: uploadedFile ? [uploadedFile] : [],
    accept: "image/*,video/*,.pdf,application/pdf",
    beforeUpload: (file: File) => {
      handleFileUpload(file);
      return false; // Prevent auto upload
    },
    onChange: (info: any) => {
      if (info.fileList.length > 0) {
        setUploadedFile(info.fileList[0]);
      } else {
        setUploadedFile(null);
        setUploadedFileUrl("");
      }
    },
    onRemove: () => {
      setUploadedFile(null);
      setUploadedFileUrl("");
    },
    onDrop: (e: any) => {},
  };

  useEffect(() => {
    if (isAdded) {
      ToastService.success("Added to Lot");
      setErrorInfluencers([]);
      onClose();
    }
  }, [isAdded]);

  if (isFetching) {
    return (
      <Row justify={"center"} style={{ marginTop: 100 }}>
        <Spin />
      </Row>
    );
  }

  return (
    <div className={styles.purchaseConfirmation}>
      <Row gutter={8}>
        {/* Left Side - Table */}
        <Col span={16}>
          <Table
            size="small"
            columns={columns}
            dataSource={dataWithTotal}
            pagination={false}
            scroll={{ x: 800, y: "70vh" }}
            rowClassName={(record) =>
              classNames(
                record.key === "total" ? styles.totalRow : "",
                errorInfluencers
                  .map((influencer) => influencer.briefInfluencer._id)
                  .includes(record.key)
                  ? styles.errorRow
                  : ""
              )
            }
          />
        </Col>

        {/* Right Side - Panel */}
        <Col span={8}>
          {isPurchaseSummary ? (
            // Purchase Summary State
            <Card title="Purchase Summary" className={styles.summaryCard}>
              <Space direction="vertical" size={0} style={{ width: "100%" }}>
                <div className={styles.summaryItem}>
                  <Text>Total Brand Price</Text>
                  <Text strong>₹{formatData(totals.brandPrice, "0.00 ")}</Text>
                </div>
                <div className={styles.summaryItem}>
                  <Text>Total Influencer Cost</Text>
                  <Text strong>
                    ₹{formatData(totals.influencerCost, "0.00 ")}
                  </Text>
                </div>
                <div className={styles.summaryItem}>
                  <Text>Total Service Cost</Text>
                  <Text strong>₹{formatData(totals.serviceCost, "0.00 ")}</Text>
                </div>
                <div className={`${styles.summaryItem} `}>
                  <Text>Total Revenue</Text>
                  <Text strong>
                    ₹{formatData(totals.revenue, "0.00 ")} (
                    {totalRevenuePercentage.toFixed(1)}%)
                  </Text>
                </div>
                <Divider style={{ margin: "16px 0" }} />

                <Button
                  type="primary"
                  size="large"
                  block
                  onClick={handlePurchaseClick}
                  disabled={toPurchaseInfluencers.length === 0}
                >
                  Purchase on behalf of Brand
                </Button>

                <div className={styles.infoMessage}>
                  <InfoCircleOutlined style={{ paddingTop: "6px" }} />
                  <Text type="secondary">
                    You need to attach payment proof received from the brand in
                    next step.
                  </Text>
                </div>
              </Space>
            </Card>
          ) : (
            // Attach Purchase Proof State
            <Card
              title="Attach Purchase Proof"
              styles={{ body: { padding: 8 } }}
            >
              <Space direction="vertical" size={2} style={{ width: "100%" }}>
                <Dragger
                  {...uploadProps}
                  className={styles.uploadArea}
                  disabled={isUploading}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    {isUploading
                      ? "Uploading..."
                      : uploadedFile
                        ? `File: ${uploadedFile.name}`
                        : "Click or drag file to this area to upload"}
                  </p>
                  <p className="ant-upload-hint">
                    {isUploading
                      ? "Please wait while your file is being uploaded"
                      : "Support for single file upload (PDF, images, videos)"}
                  </p>
                </Dragger>

                <div>
                  <Text strong>Remarks</Text>
                  <TextArea
                    placeholder="Add Remarks ..."
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    rows={4}
                    style={{ marginTop: "2px" }}
                  />
                </div>

                <Button
                  type="primary"
                  size="large"
                  block
                  style={{ marginTop: "4px" }}
                  onClick={handleSendForApproval}
                  loading={isUploading || isAdding}
                  disabled={
                    !uploadedFileUrl || toPurchaseInfluencers.length === 0
                  }
                >
                  {isUploading ? "Uploading..." : "Send for Approval"}
                </Button>

                <div className={styles.infoMessage}>
                  <InfoCircleOutlined style={{ paddingTop: "6px" }} />
                  <Text type="secondary">
                    Kindly attach a purchase proof that covers all the
                    influencers listed in the table. Next, the finance team
                    would need to approve this to confirm the purchase.
                  </Text>
                </div>
                {errorInfluencers.length > 0 && (
                  <Flex vertical gap={2}>
                    <div className={styles.infoMessage}>
                      <WarningFilled style={{ paddingTop: "6px" }} />
                      <Text type="secondary">
                        These influencers can't be purchased, please remove them
                        from the lot.
                      </Text>
                    </div>
                    <div
                      style={{
                        maxHeight: "200px",
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      {errorInfluencers.map((influencer) => (
                        <Alert
                          showIcon
                          key={influencer.briefInfluencer._id}
                          message={
                            <TextWithTooltip
                              isOnHover={true}
                              text={
                                influencer.briefInfluencer.influencerName +
                                " : " +
                                influencer.reason
                              }
                              limit={50}
                            />
                          }
                          type="error"
                        />
                      ))}
                    </div>
                  </Flex>
                )}
              </Space>
            </Card>
          )}
        </Col>
      </Row>
      {/* <Modal
        title={"View Deliverable"}
        open={deliverableModalProps.visible}
        onCancel={() =>
          setDeliverableModalProps({
            visible: false,
            type: "create",
            deliverableId: "",
            briefInfluencerIds: [],
          })
        }
        footer={null}
        width={1000}
        destroyOnClose
      >
        {deliverableModalProps.visible && (
          <>
            <DeliverablePackageCard
              briefId={brief?._id || ""}
              defaultDeliverableId={brief?.defaultDeliverableV2Id}
              deliverableId={deliverableModalProps?.deliverableId || ""}
              onClose={() =>
                setDeliverableModalProps({
                  visible: false,
                  type: "create",
                  deliverableId: "",
                  briefInfluencerIds: [],
                })
              }
            />
          </>
        )}
      </Modal> */}
    </div>
  );
}
